import { useRef, useState } from 'react'
import type { ProductSegmentRule, SegmentCondition, SegmentTopN } from './segmentPresets'

const DEFAULT_CONDITION: SegmentCondition = { field: 'revenue_30d_min', op: 'gte', value: 0 }

/** Owns segment rule state + stable per-condition keys (parallel list, not on the persisted type). */
export function useSegmentRuleState(initialRule?: ProductSegmentRule) {
  const [rule, setRule] = useState<ProductSegmentRule>(
    initialRule ?? { match: 'all', conditions: [] },
  )
  const initialCount = initialRule?.conditions.length ?? 0
  const [keys, setKeys] = useState<number[]>(() =>
    Array.from({ length: initialCount }, (_, i) => i),
  )
  const seq = useRef(initialCount)

  function addCondition() {
    setRule((r) => ({ ...r, conditions: [...r.conditions, { ...DEFAULT_CONDITION }] }))
    setKeys((k) => [...k, seq.current++])
  }

  function updateCondition(idx: number, c: SegmentCondition) {
    setRule((r) => ({ ...r, conditions: r.conditions.map((e, i) => (i === idx ? c : e)) }))
  }

  function removeCondition(idx: number) {
    setRule((r) => ({ ...r, conditions: r.conditions.filter((_, i) => i !== idx) }))
    setKeys((k) => k.filter((_, i) => i !== idx))
  }

  function setTop(top: SegmentTopN | undefined) {
    setRule((r) => ({ ...r, top }))
  }

  return { rule, keys, addCondition, updateCondition, removeCondition, setTop }
}
